import type { KeyboardEvent, MouseEvent, RefObject } from 'react'
import { haptic } from './haptic'
import type { SwipeOptions } from './recognisers'
import { pressHolds, usePointerGestures } from './usePointerGestures'

/** An action sheet asked for: where it opens and where focus goes back to (see components/ActionSheet). */
export interface SheetRequest {
  /** Where the menu opens on wider screens: at the pointer, or under the control that opened it. */
  at: { x: number; y: number }
  /** Focus returns here when the sheet closes: the control that opened it, or the row. */
  returnFocus: HTMLElement | null
  /** Opened with a finger or pen: there is a Cancel button whatever the screen size. */
  touch: boolean
  /** What a touch screen lifts in place instead of sliding a sheet up: the bubble held, where it sits. */
  anchor?: HTMLElement | null
}

/** Where a menu opened from a control goes: under it. */
export function pointBelow(element: Element): SheetRequest['at'] {
  const rect = element.getBoundingClientRect()
  return { x: rect.left, y: rect.bottom }
}

interface Options {
  /** Focus returns here when a sheet opened by a hold or a right-click closes: the row, or the link in it. */
  focusRef: RefObject<HTMLElement | null>
  /** Rows without actions (a message still sending) neither hold nor open a sheet. */
  enabled: boolean
  /** The sheet is showing: a right-click does not open it again. */
  open: boolean
  swipe?: SwipeOptions | null
  /** A right-click here keeps the browser's own menu (e.g. on a link in a message). */
  keepBrowserMenu?: (event: MouseEvent<HTMLElement>) => boolean
  onHold?: () => void
  onOpen: (request: SheetRequest) => void
}

/**
 * What opens a row's action sheet besides its own buttons (spec §3.3, §3.6): a hold (or the phone's long-press menu,
 * which comes first), a right-click, and the Menu key or Shift+F10. Spread `rowProps` on the row and call `openFrom`
 * from the row's actions button.
 */
export function useSheetTriggers({ focusRef, enabled, open, swipe = null, keepBrowserMenu, onHold, onOpen }: Options) {
  const gestures = usePointerGestures({
    swipe,
    hold: enabled
      ? {
          onHold: (start) => {
            haptic()
            onHold?.()
            onOpen({ at: { x: start.clientX, y: start.clientY }, returnFocus: focusRef.current, touch: true })
          },
        }
      : null,
  })
  const pointerIsTouch = () => {
    const type = gestures.lastPress.current?.pointerType
    return type === 'touch' || type === 'pen'
  }

  const onContextMenu = (event: MouseEvent<HTMLElement>) => {
    // A long press with a finger or pen is the hold; what is left is right-clicks (a pen's barrel button too) and the
    // keyboard.
    gestures.handlers.onContextMenu(event)
    if (event.defaultPrevented || pressHolds(gestures.lastPress.current)) return
    if (!enabled || !event.currentTarget.contains(event.target as Node) || keepBrowserMenu?.(event)) return
    event.preventDefault()
    if (!open) onOpen({ at: { x: event.clientX, y: event.clientY }, returnFocus: focusRef.current, touch: pointerIsTouch() })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!enabled || !event.currentTarget.contains(event.target as Node)) return
    if (event.key !== 'ContextMenu' && !(event.shiftKey && event.key === 'F10')) return
    event.preventDefault()
    const from = event.target as HTMLElement
    onOpen({ at: pointBelow(from), returnFocus: from, touch: false })
  }

  return {
    rowProps: { ...gestures.handlers, onContextMenu, onKeyDown },
    /** The row's actions button: focus comes back to the button. */
    openFrom: (button: HTMLElement) => onOpen({ at: pointBelow(button), returnFocus: button, touch: pointerIsTouch() }),
  }
}
